import _typeof from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/typeof.js";
import _slicedToArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _toConsumableArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import { format, parseISO } from "date-fns";
import { isEqual } from "lodash";
var fmtDate = function fmtDate(iso) {
  if (!iso) return "";
  try {
    return format(parseISO(iso), "MMM d, yyyy");
  } catch (_unused) {
    return "";
  }
};

// Render a custom field response into a single display string.
var formatTagValue = function formatTagValue(field, value) {
  if (value == null || value === "") return "";
  switch (field.tag_type) {
    case "boolean":
      return value ? "Yes" : "No";
    case "date":
      try {
        return format(new Date(value), "d MMM yyyy");
      } catch (_unused2) {
        return String(value);
      }
    case "datetime":
      try {
        return format(new Date(value), "d MMM yyyy, h:mm a");
      } catch (_unused3) {
        return String(value);
      }
    case "multi_dropdown":
    default:
      return Array.isArray(value) ? value.join(", ") : String(value);
  }
};
var mapCustomTagsToAITags = function mapCustomTagsToAITags(customTags, customFields, patientTags) {
  return (customTags !== null && customTags !== void 0 ? customTags : []).map(function (tag) {
    var field = customFields.find(function (f) {
      return (f === null || f === void 0 ? void 0 : f.id) != null && String(f.id) === String(tag.tag_id);
    });
    if (!field) return null;
    var formatted = formatTagValue(field, tag.response);
    var applied = patientTags.some(function (pt) {
      return String(pt.tag_id) === String(tag.tag_id) && isEqual(pt.response, tag.response);
    });
    return {
      id: String(tag.tag_id),
      type: "indication",
      label: formatted ? "".concat(field.tag, ": ").concat(formatted) : field.tag,
      tagId: Number(tag.tag_id),
      response: tag.response,
      applied: applied
    };
  }).filter(function (t) {
    return t !== null;
  });
};
var personName = function personName(person) {
  if (!person) return "";
  return [person.prefix, person.given, person.middle, person.family].filter(Boolean).join(" ").trim();
};
var personNames = function personNames(people) {
  return (people !== null && people !== void 0 ? people : []).map(personName).filter(Boolean);
};
var mapVersion = function mapVersion(version, customFields, patientTags) {
  var _version$order_metada, _version$report_metad, _version$visit_metada, _order$accession_numb, _visit$referring_doct, _order$ordering_provi, _visit$patient_class$, _visit$patient_class, _version$additional_m;
  var order = (_version$order_metada = version.order_metadata) !== null && _version$order_metada !== void 0 ? _version$order_metada : {};
  var report = (_version$report_metad = version.report_metadata) !== null && _version$report_metad !== void 0 ? _version$report_metad : {};
  var visit = (_version$visit_metada = version.visit_metadata) !== null && _version$visit_metada !== void 0 ? _version$visit_metada : {};
  var radiologists = [personName(report.principal_reporting_radiologist)].concat(_toConsumableArray(personNames(report.assistant_reporting_radiologists))).filter(Boolean);
  var metadata = {
    acc: (_order$accession_numb = order.accession_number) !== null && _order$accession_numb !== void 0 ? _order$accession_numb : "",
    radiologists: radiologists,
    referring: personName((_visit$referring_doct = visit.referring_doctors) === null || _visit$referring_doct === void 0 ? void 0 : _visit$referring_doct[0]),
    ordering: personName((_order$ordering_provi = order.ordering_providers) === null || _order$ordering_provi === void 0 ? void 0 : _order$ordering_provi[0]),
    patientClass: (_visit$patient_class$ = (_visit$patient_class = visit.patient_class) === null || _visit$patient_class === void 0 ? void 0 : _visit$patient_class.display) !== null && _visit$patient_class$ !== void 0 ? _visit$patient_class$ : "",
    facility: ""
  };
  var additional = {};
  Object.entries((_version$additional_m = version.additional_metadata) !== null && _version$additional_m !== void 0 ? _version$additional_m : {}).forEach(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      value = _ref2[1];
    if (value != null && _typeof(value) === "object" && !Array.isArray(value)) {
      return;
    }
    if (value != null && String(value).trim() !== "") {
      additional[key] = String(value);
    }
  });
  return {
    id: String(version.version_id),
    label: version.report_status || "Report",
    // `||` (not `??`) so empty strings also fall through to the next candidate.
    date: fmtDate(report.report_observation_datetime || version.created_at || version.updated_at),
    reportHtml: version.highlighted_report || version.report_text || "",
    metadata: metadata,
    additional: additional,
    aiTags: mapCustomTagsToAITags(version.custom_tags, customFields, patientTags)
  };
};
export var mapApiReportToCardData = function mapApiReportToCardData(report) {
  var _report$modality, _report$versions, _report$versions2, _report$updated_at;
  var customFields = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var patientTags = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  return {
    modality: (_report$modality = report.modality) !== null && _report$modality !== void 0 ? _report$modality : "",
    examDescription: report.heading || ((_report$versions = report.versions) === null || _report$versions === void 0 || (_report$versions = _report$versions[0]) === null || _report$versions === void 0 || (_report$versions = _report$versions.order_metadata) === null || _report$versions === void 0 ? void 0 : _report$versions.exam_description) || "",
    examDate: fmtDate(report.exam_observation_datetime),
    // study_id present implies linked DICOM imaging
    hasDicom: !!report.study_id,
    studyId: report.study_id,
    versions: ((_report$versions2 = report.versions) !== null && _report$versions2 !== void 0 ? _report$versions2 : []).slice().reverse().map(function (version) {
      return mapVersion(version, customFields, patientTags);
    }),
    timestamp: (_report$updated_at = report.updated_at) !== null && _report$updated_at !== void 0 ? _report$updated_at : ""
  };
};